import { proxy, subscribe, useSnapshot } from 'valtio';
import type { Maybe } from 'yup';

import type {
  CheckoutFragment,
  ProductVariantWatchBasicDetailsFragment,
} from '@/graphql/saleor/api';
import type { StoreHit } from '@/integrations/algolia/types';
import type {
  DeliveryMethod,
  ReturnMethod,
  SelectedBox,
} from '@/lib/service/type';
import { StorageHelper } from '@/lib/storage';
import type { Warranty } from '@/lib/types';
import type { Step } from '@/views/ServiceRequestView/components/ServiceStepper';

type ServiceRequestFragment = Pick<
  CheckoutFragment,
  'shippingAddress' | 'billingAddress'
>;
const storageHelper = new StorageHelper();
const localServiceRequest = storageHelper.getSessionStorage('SERVICE_REQUEST');

export type ServiceReason = {
  label: string;
  value: string;
};
export interface ServiceRequestState extends ServiceRequestFragment {
  deliveryBoutique: Maybe<StoreHit>;
  deliveryMethod: DeliveryMethod;
  email: string;
  productVariantDetails: Maybe<ProductVariantWatchBasicDetailsFragment>;
  reasonComment: Maybe<string>;
  reasons: ServiceReason[];
  returnMethod: ReturnMethod;
  selectedBox: Maybe<SelectedBox>;
  step: Step;
  watchWarranty: Maybe<Warranty>;
}

export const initialState: ServiceRequestState = {
  deliveryMethod: 'send',
  selectedBox: 'BreitlingSending',
  deliveryBoutique: null,
  email: '',
  reasons: [],
  reasonComment: '',
  shippingAddress: null,
  billingAddress: null,
  returnMethod: 'clientShipping',
  productVariantDetails: null,
  watchWarranty: null,
  step: 'watch',
};

export const serviceRequestState = proxy<ServiceRequestState>(
  localServiceRequest || {
    ...initialState,
  },
);

export const useServiceRequest = () =>
  useSnapshot<ServiceRequestState>(serviceRequestState);

export const serviceRequestActions = {
  clear: (key?: keyof ServiceRequestState) => {
    // valtio.pmnd.rs/docs/how-tos/how-to-reset-state
    if (key) {
      // @ts-ignore
      serviceRequestState[key] = initialState[key];
    } else {
      Object.keys(initialState).forEach((key) => {
        // @ts-ignore
        serviceRequestState[key as keyof ServiceRequestState] =
          initialState[key as keyof ServiceRequestState];
      });
    }
  },
};

subscribe(serviceRequestState, () => {
  storageHelper.setSessionStorage('SERVICE_REQUEST', serviceRequestState);
});
